import { useRouter } from "next/router";

import { useMemo } from "react";

type LocaleOptions = "en" | "fr" | "de";

type Routes = {
    [routeName: string]: Partial<Record<LocaleOptions, string>>;
};

type Localised<T, O> = {
    locale: T;
    defaultLocale: T;
    locales: O[];
};

const useCustomerRouter = <T, O>(): Localised<T, O> => {
    const { locale, defaultLocale, locales } = useRouter();

    return {
        locale,
        defaultLocale,
        locales
    } as Localised<T, O>;
};

const useAlternateLinkTo = (
    pathName: string,
    routes: Routes,
    baseRoute: `/${string}` = "/"
) => {
    const { defaultLocale, locales } = useCustomerRouter<"en", LocaleOptions>();

    return useMemo(
        () =>
            locales?.map((hrefLang) => {
                const parts: string[] = [];
                if (hrefLang !== defaultLocale) {
                    parts.push(`${hrefLang}`);
                }

                const baseRouteParts = baseRoute.split("/").filter((p) => p !== "");
                parts.push(...baseRouteParts);

                if (baseRouteParts.length > 0 && pathName === "/") {
                    // lookup "/" in routes
                    // replace last part of baseRoute with the lookup
                    const pathLookedUp = routes?.["/"]?.[hrefLang];

                    if (pathLookedUp) {
                        parts[parts.length - 1] = pathLookedUp;
                    }
                } else {
                    const pathLookedUp = routes?.[pathName]?.[hrefLang];

                    if (pathLookedUp !== undefined) {
                        parts.push(pathLookedUp);
                    } else if (pathName !== "/") {
                        parts.push(pathName);
                    }
                }

                const href = `/${parts.join("/")}`;
                return {
                    href,
                    hrefLang
                };
            }),
        [pathName, routes, baseRoute, defaultLocale, locales]
    );

    //     // .filter((link) => link.hrefLang !== locale);
};

export default useAlternateLinkTo;
